import { z } from 'zod';
import { percentualDe, reaisParaCentavos, type Centavos } from './dinheiro.js';

/**
 * A Ponte é composta por 100 cadeiras distribuídas em três camadas.
 * Valores e quantidades vêm do protótipo original (docs/prototipo/index.html) e da
 * Carta de Intenções — não altere sem alterar também o texto contratual.
 */
export const CAMADAS_IDS = ['movimento', 'socio', 'circulo'] as const;
export const camadaSchema = z.enum(CAMADAS_IDS);
export type CamadaId = z.infer<typeof camadaSchema>;

export interface Camada {
  id: CamadaId;
  nome: string;
  /** Investimento anual da cadeira, em centavos. */
  valor: Centavos;
  /** Quantidade de cadeiras existentes na camada. */
  cadeiras: number;
}

export const CAMADAS: Record<CamadaId, Camada> = {
  movimento: {
    id: 'movimento',
    nome: 'Movimento Prosperar',
    valor: reaisParaCentavos(120_000),
    cadeiras: 55,
  },
  socio: { id: 'socio', nome: 'Sócio', valor: reaisParaCentavos(240_000), cadeiras: 30 },
  circulo: { id: 'circulo', nome: 'Círculo', valor: reaisParaCentavos(360_000), cadeiras: 15 },
};

export const TOTAL_DE_CADEIRAS = Object.values(CAMADAS).reduce((s, c) => s + c.cadeiras, 0); // 100

/** Sinal pago junto da Carta de Intenções, à vista, sem parcelamento. */
export const PERCENTUAL_SINAL = 5;
/** Saldo quitado após a aprovação em curadoria, no Termo de Aceite. */
export const PERCENTUAL_SALDO = 100 - PERCENTUAL_SINAL;

/** Prazo, em dias corridos, para pagamento do sinal antes de a pré-reserva cair. */
export const DIAS_VENCIMENTO_SINAL = 3;
/** Prazo, em dias corridos, para pagamento do saldo. */
export const DIAS_VENCIMENTO_SALDO = 7;

export interface ValoresDaCadeira {
  total: Centavos;
  sinal: Centavos;
  saldo: Centavos;
}

/**
 * Sinal e saldo de uma camada, **pela tabela vigente hoje**.
 *
 * Para candidato que já teve a Carta emitida, não use esta função: use
 * `valoresVigentes(candidato)`, que devolve o valor congelado no contrato. A tabela viva
 * só vale para ficha que ainda não virou documento.
 *
 * O saldo é o resto da subtração — nunca um segundo arredondamento — para que
 * sinal + saldo seja sempre exatamente o total.
 */
export function valoresDaCamada(camada: CamadaId): ValoresDaCadeira {
  const total = CAMADAS[camada].valor;
  const sinal = percentualDe(total, PERCENTUAL_SINAL);
  return { total, sinal, saldo: total - sinal };
}

/**
 * Número da cadeira: inteiro de 1 até o total da camada, normalizado com três dígitos.
 *
 * A normalização importa além da estética: a reserva exclusiva usa
 * `CADEIRA#<camada>#<numero>` como chave, e "1" e "001" seriam **duas cadeiras
 * diferentes** — dois integrantes com a mesma cadeira e nenhum erro aparente.
 */
export function normalizarNumeroDaCadeira(entrada: string): string {
  return String(Number(entrada.trim())).padStart(3, '0');
}

export interface NumeroDaCadeiraInvalido {
  motivo: string;
}

/** `null` quando o número é aceitável para a camada. */
export function validarNumeroDaCadeira(
  camada: CamadaId,
  entrada: string,
): NumeroDaCadeiraInvalido | null {
  const limpo = entrada.trim();
  if (limpo === '') return null; // número em branco é permitido até a cadeira ser ocupada

  if (!/^\d{1,3}$/.test(limpo)) {
    return { motivo: 'Informe apenas dígitos, sem letras nem pontuação.' };
  }

  const numero = Number(limpo);
  const { nome, cadeiras } = CAMADAS[camada];

  if (numero < 1 || numero > cadeiras) {
    return {
      motivo: `A camada ${nome} vai da cadeira 001 até a ${String(cadeiras).padStart(3, '0')}.`,
    };
  }

  return null;
}
